import { type PaginationState, TablePagination } from '@/components/shared/TablePagination';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import { Archive, Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DocumentStatusBadge from '@/modules/org/contracts/components/document-status-badge';
import type { ContractDocumentRow } from '@/modules/org/contracts/constants/mock-contract-documents';

interface ArchiveDataCardViewProps {
  documents: ContractDocumentRow[];
  isLoading?: boolean;
  loadingRowCount?: number;
  emptyMessage?: string;
  /** Per-row loading state — driven by the same updatingItemId + action
      flags the desktop table reads. The parent passes them down so card
      buttons spin in lockstep with table buttons. */
  isUpdating?: boolean;
  updatingItemId?: string | null;
  updatingAction?: 'unarchive' | 'move-to-trash' | null;
  onUnarchive: (row: ContractDocumentRow) => void;
  onMoveToTrash: (row: ContractDocumentRow) => void;
  pagination?: PaginationState;
  onPageChange?: (page: number) => void;
  showingLabel?: string;
  className?: string;
}

const ArchiveDataCardView = ({
  documents,
  isLoading = false,
  loadingRowCount = 5,
  emptyMessage,
  isUpdating = false,
  updatingItemId,
  updatingAction,
  onUnarchive,
  onMoveToTrash,
  pagination,
  onPageChange,
  showingLabel,
  className,
}: ArchiveDataCardViewProps) => {
  const { t } = useTranslation();
  const skeletonRows = Math.max(loadingRowCount, 1);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-background',
        className
      )}
    >
      <div className="flex flex-col gap-3 p-3">
        {isLoading ? (
          Array.from({ length: skeletonRows }).map((_, i) => (
            <div
              key={`archive-card-skeleton-${i}`}
              className="rounded-lg border border-border bg-background p-4 space-y-3"
            >
              <Skeleton className="h-4 w-3/4 rounded-full" />
              <Skeleton className="h-3 w-1/2 rounded-full" />
              <Skeleton className="h-3 w-2/3 rounded-full" />
            </div>
          ))
        ) : documents.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            {emptyMessage ?? t('ORGARCHIVE_EMPTY')}
          </div>
        ) : (
          documents.map((row) => {
            const isRestoreLoading =
              isUpdating && updatingItemId === row.id && updatingAction === 'unarchive';
            const isDeleteLoading =
              isUpdating && updatingItemId === row.id && updatingAction === 'move-to-trash';
            const anyLoading = isRestoreLoading || isDeleteLoading;

            return (
              <article
                key={row.id}
                className="rounded-lg border border-border bg-background p-4 space-y-4"
              >
                {/* Top row: icon tile + title/subtitle, status badge in the
                    top-right corner (mirrors the template-card layout). */}
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-lg bg-selise-blue/10 flex items-center justify-center">
                    <Archive className="h-5 w-5 text-selise-blue" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold truncate text-[hsl(var(--fg))]">
                        {row.title}
                      </h3>
                      <div className="shrink-0">
                        <DocumentStatusBadge status={row.status} />
                      </div>
                    </div>
                    <p className="text-sm text-globe-grey truncate">
                      {row.recipientsSummary ?? '-'}
                    </p>
                  </div>
                </div>

                {/* Metadata grid — Created + Archived timestamps surfaced
                    side-by-side. `lastEditedAt` doubles as "archived at"
                    here because the backend bumps `LastUpdatedDate` when
                    a doc is archived (no dedicated ArchivedAt column). */}
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div className="min-w-0">
                    <dt className="text-globe-grey">
                      {t('ORGCONTRACTS_COLUMNS_CREATEDDATE')}
                    </dt>
                    <dd className="font-medium text-[hsl(var(--fg))] truncate">
                      {row.createdAt ?? '-'}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-globe-grey">
                      {t('ORGARCHIVE_COLUMNS_ARCHIVEDAT', 'Archived')}
                    </dt>
                    <dd className="font-medium text-[hsl(var(--fg))] truncate">
                      {row.lastEditedAt ?? '-'}
                    </dd>
                  </div>
                </dl>

                {/* Divider extending to card edges, then two equal-width
                    action buttons at the bottom. */}
                <div className="-mx-4 border-t border-border" />

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-selise-blue-500 hover:text-selise-blue-500/90"
                    onClick={() => onUnarchive(row)}
                    disabled={anyLoading}
                  >
                    {isRestoreLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    {t('ORGARCHIVE_ACTION_RESTORE')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-destructive hover:text-destructive/90"
                    onClick={() => onMoveToTrash(row)}
                    disabled={anyLoading}
                  >
                    {isDeleteLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    {t('ORGARCHIVE_ACTION_DELETE')}
                  </Button>
                </div>
              </article>
            );
          })
        )}
      </div>

      {!isLoading && pagination && onPageChange ? (
        <div className="border-t border-border bg-background px-3 py-2">
          <TablePagination
            pagination={pagination}
            onPageChange={onPageChange}
            showingLabel={showingLabel}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ArchiveDataCardView;
